import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Loader2, Save, Upload } from 'lucide-react';

import { invPhoto, checkPhoto, uploadInventoryPhoto } from './photos';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CATEGORIES = ['Chemicals', 'Tools', 'Consumables'];


const empty = {
  name: '', brand: '', category: 'Chemicals', photo_url: '',
  vendor_name: '', purchase_url: '', store_address: '',
  current_replacement_cost: '', date_purchased: '', initial_purchase_price: '',
  quantity_on_hand: '', reorder_level: '', current_location: '', notes: '',
};

const AddProductDialog = ({ open, onOpenChange, headers, product = null, onSaved }) => {
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    if (open) setPhotoFile(null);
    if (open) setForm(product ? { ...empty, ...product } : empty);
  }, [open, product]);

  const onPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const err = checkPhoto(f);
    if (err) { toast.error(err); return; }
    setPhotoFile(f);
    set('photo_url', URL.createObjectURL(f));
  };

  const save = async () => {
    if (!form.name || !form.category) { toast.error('Name and category are required'); return; }
    setBusy(true);
    try {
      const { photo_url: _photo, ...payload } = form;
      ['current_replacement_cost', 'initial_purchase_price', 'quantity_on_hand', 'reorder_level'].forEach(k => {
        if (payload[k] === '' || payload[k] == null) delete payload[k];
        else payload[k] = parseFloat(payload[k]);
      });
      Object.keys(payload).forEach(k => { if (payload[k] === '') delete payload[k]; });
      if (product?.id) {
        await axios.patch(`${API_URL}/api/admin/inventory/products/${product.id}`, payload, { headers });
        if (photoFile) await uploadInventoryPhoto('product', product.id, photoFile, headers);
        toast.success('Product updated');
      } else {
        const created = await axios.post(`${API_URL}/api/admin/inventory/products`, payload, { headers });
        if (photoFile) await uploadInventoryPhoto('product', created.data.id, photoFile, headers);
        toast.success('Product added');
      }
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="add-product-dialog">
        <DialogHeader><DialogTitle>{product ? 'Edit product' : 'Add product'}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {/* Identity */}
          <Section title="Product Identity">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Product Name *"><Input value={form.name} onChange={e => set('name', e.target.value)} data-testid="prod-name" /></Field>
              <Field label="Brand"><Input value={form.brand} onChange={e => set('brand', e.target.value)} data-testid="prod-brand" /></Field>
              <Field label="Category *">
                <Select value={form.category} onValueChange={v => set('category', v)}>
                  <SelectTrigger data-testid="prod-cat"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Photo (≤2 MB)">
                <div className="flex items-center gap-2">
                  {form.photo_url && <img src={invPhoto(form.photo_url)} alt="" className="w-16 h-16 rounded bg-[#f4f2ed] object-contain" />}
                  <label className="flex-1 inline-flex items-center justify-center gap-1 border border-dashed border-zinc-300 rounded-lg py-2 text-xs cursor-pointer hover:bg-zinc-50">
                    <Upload className="w-3 h-3" /> {form.photo_url ? 'Change photo' : 'Upload photo'}
                    <input type="file" accept="image/jpeg,image/png,image/webp" onChange={onPhoto} className="hidden" data-testid="prod-photo" />
                  </label>
                </div>
              </Field>
            </div>
          </Section>
          {/* Purchase */}
          <Section title="Purchasing Information">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Vendor / Store"><Input value={form.vendor_name} onChange={e => set('vendor_name', e.target.value)} data-testid="prod-vendor" /></Field>
              <Field label="Purchase URL"><Input value={form.purchase_url} onChange={e => set('purchase_url', e.target.value)} placeholder="https://..." data-testid="prod-url" /></Field>
              <Field label="Physical Store Address"><Input value={form.store_address} onChange={e => set('store_address', e.target.value)} data-testid="prod-addr" /></Field>
              <Field label="Current Replacement Cost ($)"><Input type="number" step="0.01" value={form.current_replacement_cost} onChange={e => set('current_replacement_cost', e.target.value)} data-testid="prod-repl-cost" /></Field>
              {!product && (
                <>
                  <Field label="Original Purchase Price ($)"><Input type="number" step="0.01" value={form.initial_purchase_price} onChange={e => set('initial_purchase_price', e.target.value)} data-testid="prod-initial-price" /></Field>
                  <Field label="Date Purchased"><Input type="date" value={form.date_purchased} onChange={e => set('date_purchased', e.target.value)} data-testid="prod-date" /></Field>
                </>
              )}
            </div>
          </Section>
          {/* Inventory */}
          {!product && (
            <Section title="Inventory Information">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Field label="Quantity On Hand"><Input type="number" step="1" value={form.quantity_on_hand} onChange={e => set('quantity_on_hand', e.target.value)} data-testid="prod-qty" /></Field>
                <Field label="Reorder Level"><Input type="number" step="1" value={form.reorder_level} onChange={e => set('reorder_level', e.target.value)} data-testid="prod-reorder" /></Field>
                <Field label="Current Location"><Input value={form.current_location} onChange={e => set('current_location', e.target.value)} placeholder="Supply closet, Sean..." data-testid="prod-loc" /></Field>
              </div>
            </Section>
          )}
          {product && (
            <Section title="Reorder & Location">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Reorder Level"><Input type="number" step="1" value={form.reorder_level} onChange={e => set('reorder_level', e.target.value)} /></Field>
                <Field label="Current Location"><Input value={form.current_location} onChange={e => set('current_location', e.target.value)} /></Field>
              </div>
            </Section>
          )}
          <Field label="Notes"><Textarea rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} /></Field>
          <div className="flex justify-end gap-2 pt-2 border-t border-zinc-100">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
            <Button onClick={save} disabled={busy} className="bg-[#F56F05] hover:bg-[#e06505] text-white" data-testid="prod-save">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {product ? 'Save changes' : 'Add product'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Section = ({ title, children }) => (
  <div>
    <p className="text-[11px] uppercase font-bold text-zinc-500 mb-2">{title}</p>
    {children}
  </div>
);
const Field = ({ label, children }) => (
  <div className="space-y-1"><Label className="text-xs text-zinc-600">{label}</Label>{children}</div>
);

export default AddProductDialog;
